/**
 * Runtime Configuration Service
 *
 * Fetches application configuration from a static /config.json file
 * deployed to S3 alongside the frontend assets.
 *
 * The config.json file is generated during deployment and contains:
 * - auth.publishableKey — provider-agnostic auth key (currently Clerk)
 * - apiUrl — API Gateway base URL
 * - environment — deployment stage name
 * - baseDomain — base domain for the environment
 *
 * Implements in-memory caching with automatic expiration.
 */

let appConfig = null;
let cacheTimestamp = null;
const CACHE_TTL = 300000; // 5 minutes in milliseconds

/**
 * Get application configuration from static /config.json
 * @returns {Promise<Object>} Configuration object
 */
export const getConfig = async () => {
  // Return cached config if still valid
  const now = Date.now();
  if (appConfig && cacheTimestamp && (now - cacheTimestamp < CACHE_TTL)) {
    return appConfig;
  }

  try {
    const response = await fetch('/config.json', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Config fetch failed: ${response.status} ${response.statusText}`);
    }

    const rawConfig = await response.json();
    
    // Validate required configuration fields
    if (!rawConfig.apiUrl || !rawConfig.auth?.publishableKey) {
      throw new Error('Invalid configuration: missing required fields (apiUrl, auth.publishableKey)');
    }

    const config = {
      apiBaseUrl: rawConfig.apiUrl,
      authPublishableKey: rawConfig.auth.publishableKey,
      environment: rawConfig.environment,
      baseDomain: rawConfig.baseDomain,
      tourAvailableFrom: rawConfig.tourAvailableFrom || null,
      guideContentCdnUrl: rawConfig.guideContentCdnUrl || null
    };

    // Cache the configuration
    appConfig = config;
    cacheTimestamp = now;

    return config;
  } catch (error) {
    console.error('❌ Failed to load configuration:', error);
    throw error;
  }
};

/**
 * Clear the configuration cache
 * Useful for forcing a refresh of configuration values
 */
export const clearConfigCache = () => {
  appConfig = null;
  cacheTimestamp = null;
};

/**
 * Get cached configuration without fetching
 * @returns {Object|null} Cached configuration or null
 */
export const getCachedConfig = () => {
  return appConfig;
};

export default {
  getConfig,
  clearConfigCache,
  getCachedConfig
};
